import React, { useState } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { Layout } from "./components/dashboard/layout";
import {
  Box, Typography, TextField, Button, Paper,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Alert, Stack
} from '@mui/material';

const TemporaryIDReports = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Convert 'YYYY-MM-DD' → local day start/end Date
  const toLocalDayBounds = (d) => {
    const start = new Date(`${d}T00:00:00`);
    const end   = new Date(`${d}T23:59:59.999`);
    return { start, end };
  };

  // Normalize Firestore Timestamp or ISO/string → Date
  const toDateObj = (v) => {
    if (!v) return null;
    if (typeof v?.toDate === 'function') return v.toDate(); // Timestamp
    const dt = new Date(v);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const fetchReports = async () => {
    setErr('');
  setReports([]);

  if (!startDate || !endDate) {
    setErr('Please select both start and end dates.');
    return;
  }
  if (startDate > endDate) {
    setErr('Start Date cannot be after End Date.');
    return;
  }

  setLoading(true);
  try {
    const start = new Date(`${startDate}T00:00:00`);
    const end   = new Date(`${endDate}T23:59:59.999`);
    const colRef = collection(db, 'temp_ids');

    // --- Try as Firestore Timestamp ---
    let data = [];
    try {
      const qTs = query(
        colRef,
        where('issue_time', '>=', Timestamp.fromDate(start)),
        where('issue_time', '<=', Timestamp.fromDate(end)),
        orderBy('issue_time', 'desc')
      );
      const snapTs = await getDocs(qTs);
      console.log('Timestamp query size:', snapTs.size);
      data = snapTs.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Timestamp query failed (likely not a Timestamp field):', e);
    }

    // --- If nothing, try as ISO string ---
    if (!data.length) {
      const startISO = start.toISOString();
      const endISO   = end.toISOString();
      try {
        const qStr = query(
          colRef,
          where('issue_time', '>=', startISO),
          where('issue_time', '<=', endISO),
          orderBy('issue_time', 'desc')
        );
        const snapStr = await getDocs(qStr);
        console.log('String query size:', snapStr.size);
        data = snapStr.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e2) {
        console.warn('String query also failed:', e2);
      }
    }

    // Normalize dates for display
    const toDateObj = (v) => (v && typeof v.toDate === 'function') ? v.toDate() : new Date(v);
    data.sort((a,b) => (toDateObj(b.issue_time)?.getTime()||0) - (toDateObj(a.issue_time)?.getTime()||0));
    setReports(data);

    if (!data.length) {
      setErr('No records found for the selected range. Check if "issue_time" is a Timestamp or an ISO string.');
    }
  } catch (e) {
    console.error('Error fetching reports:', e);
    setErr(e?.message || 'Failed to fetch reports.');
  } finally {
    setLoading(false);
  }
};

  const exportCSV = () => {
    if (!reports.length) return;

    const esc = (s) => {
      const v = (s ?? '').toString().replace(/"/g, '""');
      return `"${v}"`;
    };

    const rows = reports.map(r => {
      const issue = toDateObj(r.issue_time)?.toLocaleString() || '';
      const returned = !!r.returned;
      const retTime  = returned ? (toDateObj(r.return_time)?.toLocaleString() || '') : 'Pending';
      return [
        r.userName || '',
        r.userId || '',
        r.tempIDNumber || '',
        issue,
        returned ? 'Yes' : 'No',
        retTime
      ].map(esc).join(',');
    });

    const header = [
      'User Name', 'User ID', 'Temp ID Number', 'Issued At', 'Returned?', 'Return Time', 'User Type'
    ].map(esc).join(',');

    const csv = [header, ...rows].join('\n');
    // Add BOM so Excel opens UTF-8 correctly
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Temporary_ID_Reports_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <Box sx={{ p: 4, maxWidth: `100%`, width: `90%`, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>Temporary ID Reports</Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="From date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Button variant="contained" onClick={fetchReports} disabled={loading}>
            {loading ? 'Loading…' : 'Submit'}
          </Button>
          <Button variant="outlined" onClick={exportCSV} disabled={!reports.length}>
            Export CSV
          </Button>
        </Stack>

        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        <Paper elevation={1}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{fontWeight: `bold`}}>User Name</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>User ID</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>Temp ID Number</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>Issued At</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>Returned?</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>Return Time</TableCell>
                  <TableCell style={{fontWeight: `bold`}}>User Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!reports.length && !loading) ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map(r => {
                    const issue = toDateObj(r.issue_time)?.toLocaleString() || '';
                    const returned = !!r.returned;
                    const retTime = returned ? (toDateObj(r.return_time)?.toLocaleString() || '') : 'Pending';
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.userName || '-'}</TableCell>
                        <TableCell>{r.userId || '-'}</TableCell>
                        <TableCell>{r.tempIDNumber || '-'}</TableCell>
                        <TableCell>{issue}</TableCell>
                        <TableCell>{returned ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{retTime}</TableCell>
                        <TableCell>{r.userType}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Layout>
  );
};

export default TemporaryIDReports;